import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, CATEGORIAS_CLIENTE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import StatusBadge from "../components/StatusBadge";
import { Wrench, Bike, LogOut, Plus, Trash2, CalendarCheck2, X, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const TIPO_VEICULO_OPTIONS = [
    { value: "moto", label: "Moto" },
    { value: "pitbike", label: "Pitbike" },
    { value: "scooter", label: "Scooter" },
    { value: "trotinete", label: "Trotinete Elétrica" },
    { value: "outro", label: "Outro" },
];

export default function ClientDashboardPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [motos, setMotos] = useState([]);
    const [servicos, setServicos] = useState([]);
    const [slots, setSlots] = useState([]);
    const [openMoto, setOpenMoto] = useState(false);
    const [openAgendar, setOpenAgendar] = useState(false);
    const [reagendarServico, setReagendarServico] = useState(null);

    async function loadAll() {
        try {
            const [m, s, sl] = await Promise.all([
                api.get("/cliente/motos"),
                api.get("/cliente/servicos"),
                api.get("/public/slots"),
            ]);
            setMotos(m.data);
            setServicos(s.data);
            setSlots(sl.data);
        } catch (e) {
            toast.error("Erro ao carregar dados.");
        }
    }
    useEffect(() => {
        loadAll();
    }, []);

    async function handleLogout() {
        await logout();
        navigate("/", { replace: true });
    }

    function canModify(servico) {
        if (!["a_aguardar", "pendente"].includes(servico.status)) return false;
        const dt = new Date(`${servico.data}T${servico.hora || "00:00"}:00`);
        return dt.getTime() - Date.now() >= 24 * 60 * 60 * 1000;
    }

    async function handleCancel(servico) {
        if (!window.confirm(`Cancelar a marcação de ${servico.data} às ${servico.hora}?`)) return;
        try {
            await api.delete(`/cliente/servicos/${servico.id}`);
            toast.success("Marcação cancelada.");
            loadAll();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao cancelar.");
        }
    }

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="client-dashboard">
            <header className="border-b border-zinc-200 bg-white">
                <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#FF3B30] text-white">
                            <Wrench className="h-4 w-4" />
                        </div>
                        <div>
                            <div className="font-['Outfit'] text-base font-semibold text-zinc-900">
                                Olá, {user?.name?.split(" ")[0] || "Cliente"}
                            </div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                                Área do cliente
                            </div>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleLogout} className="rounded-sm" data-testid="client-logout">
                        <LogOut className="mr-2 h-4 w-4" /> Sair
                    </Button>
                </div>
            </header>

            <main className="mx-auto max-w-5xl px-6 py-8">
                <Tabs defaultValue="agendar" className="w-full">
                    <TabsList className="rounded-sm">
                        <TabsTrigger value="agendar" data-testid="tab-agendar">Marcar Revisão</TabsTrigger>
                        <TabsTrigger value="veiculos" data-testid="tab-veiculos">Meus Veículos</TabsTrigger>
                        <TabsTrigger value="historico" data-testid="tab-historico">Histórico</TabsTrigger>
                    </TabsList>

                    <TabsContent value="agendar" className="mt-6">
                        <div className="border border-zinc-200 bg-white p-6">
                            <h2 className="font-['Outfit'] text-2xl font-semibold text-zinc-900">
                                Agendar uma revisão
                            </h2>
                            <p className="mt-1 text-sm text-zinc-600">
                                Escolha um dos horários disponíveis e o veículo que vai trazer.
                            </p>
                            <Button
                                className="mt-4 rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                                onClick={() => setOpenAgendar(true)}
                                disabled={motos.length === 0 || slots.length === 0}
                                data-testid="btn-abrir-agendar"
                            >
                                <CalendarCheck2 className="mr-2 h-4 w-4" /> Marcar agora
                            </Button>
                            {motos.length === 0 ? (
                                <p className="mt-3 text-xs text-amber-800">
                                    Registe pelo menos um veículo primeiro (separador Meus Veículos).
                                </p>
                            ) : null}
                            {motos.length > 0 && slots.length === 0 ? (
                                <p className="mt-3 text-xs text-amber-800">
                                    Não há horários disponíveis de momento. Volte mais tarde.
                                </p>
                            ) : null}
                        </div>
                    </TabsContent>

                    <TabsContent value="veiculos" className="mt-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="font-['Outfit'] text-2xl font-semibold text-zinc-900">
                                Os meus veículos
                            </h2>
                            <Button onClick={() => setOpenMoto(true)} className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]" data-testid="btn-add-moto">
                                <Plus className="mr-2 h-4 w-4" /> Adicionar
                            </Button>
                        </div>
                        {motos.length === 0 ? (
                            <div className="border border-dashed border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">
                                Ainda não tem veículos registados.
                            </div>
                        ) : (
                            <div className="grid gap-3 md:grid-cols-2">
                                {motos.map((m) => (
                                    <MotoCard key={m.id} moto={m} onDeleted={loadAll} />
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="historico" className="mt-6">
                        <h2 className="mb-4 font-['Outfit'] text-2xl font-semibold text-zinc-900">
                            Histórico de marcações
                        </h2>
                        {servicos.length === 0 ? (
                            <div className="border border-dashed border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">
                                Sem marcações ainda.
                            </div>
                        ) : (
                            <div className="border border-zinc-200 bg-white">
                                {servicos.map((s) => {
                                    const modifiable = canModify(s);
                                    return (
                                    <div key={s.id} className="flex flex-col gap-3 border-b border-zinc-100 px-4 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`hist-servico-${s.id}`}>
                                        <div>
                                            <div className="font-medium text-zinc-900">{s.tipo}</div>
                                            <div className="text-xs text-zinc-500 font-mono">{s.data} · {s.hora}</div>
                                            {s.numero ? <div className="mt-0.5 inline-block rounded-sm bg-zinc-100 px-1.5 py-0.5 font-mono text-[10px] text-zinc-700">Nº {s.numero}</div> : null}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                            <StatusBadge status={s.status} />
                                            {modifiable ? (
                                                <>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 rounded-sm px-2 text-xs"
                                                        onClick={() => setReagendarServico(s)}
                                                        data-testid={`btn-reagendar-${s.id}`}
                                                    >
                                                        <CalendarClock className="mr-1 h-3 w-3" /> Reagendar
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 rounded-sm px-2 text-xs text-red-600 hover:text-red-700"
                                                        onClick={() => handleCancel(s)}
                                                        data-testid={`btn-cancelar-${s.id}`}
                                                    >
                                                        <X className="mr-1 h-3 w-3" /> Cancelar
                                                    </Button>
                                                </>
                                            ) : null}
                                            <a href={`/track/${s.tracking_token}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#FF3B30] hover:underline">
                                                Acompanhar →
                                            </a>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </main>

            <AddMotoDialog open={openMoto} onClose={() => setOpenMoto(false)} onSaved={() => { setOpenMoto(false); loadAll(); }} />
            <AgendarDialog
                open={openAgendar}
                onClose={() => setOpenAgendar(false)}
                onSaved={() => { setOpenAgendar(false); loadAll(); }}
                motos={motos}
                slots={slots}
            />
            <ReagendarDialog
                servico={reagendarServico}
                onClose={() => setReagendarServico(null)}
                onSaved={() => { setReagendarServico(null); loadAll(); }}
                motos={motos}
                slots={slots}
            />
        </div>
    );
}

function MotoCard({ moto, onDeleted }) {
    async function del() {
        if (!window.confirm("Eliminar este veículo?")) return;
        try {
            await api.delete(`/cliente/motos/${moto.id}`);
            toast.success("Veículo removido.");
            onDeleted();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        }
    }
    const typeLabel = moto.tipo_veiculo === "outro"
        ? (moto.tipo_outro_desc || "Outro")
        : (TIPO_VEICULO_OPTIONS.find(o => o.value === moto.tipo_veiculo)?.label || moto.tipo_veiculo);
    return (
        <div className="flex items-start justify-between border border-zinc-200 bg-white p-4" data-testid={`moto-card-${moto.id}`}>
            <div className="flex items-start gap-3">
                <Bike className="mt-1 h-5 w-5 text-zinc-400" />
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">{typeLabel}</div>
                    <div className="font-medium text-zinc-900">{moto.marca} {moto.modelo}</div>
                    {moto.num_chassis ? <div className="mt-0.5 font-mono text-[10px] text-zinc-500">Chassis: {moto.num_chassis}</div> : null}
                </div>
            </div>
            <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700" onClick={del} data-testid={`moto-del-${moto.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
            </Button>
        </div>
    );
}

function AddMotoDialog({ open, onClose, onSaved }) {
    const [form, setForm] = useState({ tipo_veiculo: "moto", tipo_outro_desc: "", marca: "", modelo: "", num_chassis: "" });
    const [saving, setSaving] = useState(false);
    React.useEffect(() => {
        if (open) setForm({ tipo_veiculo: "moto", tipo_outro_desc: "", marca: "", modelo: "", num_chassis: "" });
    }, [open]);
    async function save() {
        if (!form.marca.trim() || !form.modelo.trim()) {
            toast.error("Marca e modelo obrigatórios."); return;
        }
        if (form.tipo_veiculo === "outro" && !form.tipo_outro_desc.trim()) {
            toast.error("Descreva o tipo de veículo em 'Outro'."); return;
        }
        setSaving(true);
        try {
            await api.post("/cliente/motos", form);
            toast.success("Veículo adicionado.");
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        } finally { setSaving(false); }
    }
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-sm" data-testid="add-moto-dialog">
                <DialogHeader><DialogTitle>Adicionar veículo</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Tipo *</Label>
                        <Select value={form.tipo_veiculo} onValueChange={(v) => setForm({ ...form, tipo_veiculo: v })}>
                            <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="add-moto-tipo">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {TIPO_VEICULO_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {form.tipo_veiculo === "outro" ? (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Descrição do veículo *</Label>
                            <Input value={form.tipo_outro_desc} onChange={(e) => setForm({ ...form, tipo_outro_desc: e.target.value })} className="mt-1.5 h-10 rounded-sm" placeholder="Ex: Bicicleta elétrica" data-testid="add-moto-outro" />
                        </div>
                    ) : null}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Marca *</Label>
                            <Input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} className="mt-1.5 h-10 rounded-sm" data-testid="add-moto-marca" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Modelo *</Label>
                            <Input value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} className="mt-1.5 h-10 rounded-sm" data-testid="add-moto-modelo" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Nº Chassis (opcional)</Label>
                        <Input value={form.num_chassis} onChange={(e) => setForm({ ...form, num_chassis: e.target.value })} className="mt-1.5 h-10 rounded-sm font-mono" data-testid="add-moto-chassis" />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancelar</Button>
                    <Button onClick={save} disabled={saving} className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]" data-testid="add-moto-save">
                        {saving ? "A guardar..." : "Guardar"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AgendarDialog({ open, onClose, onSaved, motos, slots }) {
    const [form, setForm] = useState({ moto_id: "", slot_id: "", tipo: "Revisão Simples", descricao: "" });
    const [saving, setSaving] = useState(false);
    React.useEffect(() => {
        if (open) setForm({ moto_id: motos[0]?.id || "", slot_id: "", tipo: "Revisão Simples", descricao: "" });
    }, [open, motos]);

    // Agrupa slots por dia; para cada dia mantém o primeiro slot com vaga
    const diasDisponiveis = React.useMemo(() => {
        const map = new Map();
        (slots || []).forEach(s => {
            const vagas = (s.capacidade || 0) - (s.reservados || 0);
            if (vagas <= 0) return;
            if (!map.has(s.data)) {
                map.set(s.data, { data: s.data, slot_id: s.id, vagas_total: vagas });
            } else {
                const cur = map.get(s.data);
                cur.vagas_total += vagas;
            }
        });
        return Array.from(map.values()).sort((a, b) => (a.data > b.data ? 1 : -1));
    }, [slots]);

    async function save() {
        if (!form.moto_id) { toast.error("Escolha o veículo."); return; }
        if (!form.slot_id) { toast.error("Escolha o dia de entrega."); return; }
        if (!form.tipo) { toast.error("Escolha o tipo de serviço."); return; }
        if (form.tipo === "Outros" && !form.descricao.trim()) {
            toast.error("Descreva os serviços/sintomas quando escolhe 'Outros'.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/cliente/agendar", form);
            toast.success("Marcação criada! A oficina irá confirmar em breve.");
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao agendar");
        } finally { setSaving(false); }
    }
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-sm" data-testid="agendar-dialog">
                <DialogHeader><DialogTitle>Marcar revisão</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Veículo</Label>
                        <Select value={form.moto_id} onValueChange={(v) => setForm({ ...form, moto_id: v })}>
                            <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="agendar-moto">
                                <SelectValue placeholder="Selecionar" />
                            </SelectTrigger>
                            <SelectContent>
                                {motos.map(m => <SelectItem key={m.id} value={m.id}>{m.marca} {m.modelo}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Dia de entrega</Label>
                        <Select value={form.slot_id} onValueChange={(v) => setForm({ ...form, slot_id: v })}>
                            <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="agendar-slot">
                                <SelectValue placeholder="Selecionar dia" />
                            </SelectTrigger>
                            <SelectContent>
                                {diasDisponiveis.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-zinc-500">Sem dias disponíveis</div>
                                ) : diasDisponiveis.map(d => (
                                    <SelectItem key={d.slot_id} value={d.slot_id}>
                                        {d.data}  ({d.vagas_total} vaga{d.vagas_total !== 1 ? "s" : ""})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="mt-1 text-[10px] text-zinc-500">A oficina irá confirmar o horário do dia após aprovar a marcação.</p>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Tipo de serviço</Label>
                        <div className="mt-1.5 grid grid-cols-3 gap-2">
                            {CATEGORIAS_CLIENTE.map(cat => {
                                const active = form.tipo === cat;
                                return (
                                    <button
                                        key={cat}
                                        type="button"
                                        onClick={() => setForm({ ...form, tipo: cat })}
                                        data-testid={`agendar-tipo-${cat.toLowerCase().replace(/\s/g, "-")}`}
                                        className={`h-10 border text-xs font-semibold transition-all ${
                                            active
                                                ? "border-[#FF3B30] bg-[#FF3B30] text-white"
                                                : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"
                                        }`}
                                    >
                                        {cat}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {form.tipo === "Outros" ? (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                                Descreva os serviços e sintomas *
                            </Label>
                            <Textarea
                                rows={4}
                                value={form.descricao}
                                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                                className="mt-1.5 rounded-sm"
                                placeholder="Ex: Motor faz barulho ao acelerar, luz de aviso do óleo acesa..."
                                data-testid="agendar-descricao"
                            />
                        </div>
                    ) : (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Notas adicionais (opcional)</Label>
                            <Textarea
                                rows={2}
                                value={form.descricao}
                                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                                className="mt-1.5 rounded-sm"
                                placeholder="Alguma indicação extra para a oficina"
                                data-testid="agendar-descricao"
                            />
                        </div>
                    )}
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancelar</Button>
                    <Button onClick={save} disabled={saving} className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]" data-testid="agendar-save">
                        {saving ? "A marcar..." : "Confirmar marcação"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function ReagendarDialog({ servico, onClose, onSaved, motos, slots }) {
    const open = !!servico;
    const [form, setForm] = useState({ moto_id: "", slot_id: "", tipo: "Revisão Simples", descricao: "" });
    const [saving, setSaving] = useState(false);

    React.useEffect(() => {
        if (servico) {
            const tipoInicial = CATEGORIAS_CLIENTE.includes(servico.tipo) ? servico.tipo : "Outros";
            setForm({
                moto_id: servico.moto_id || "",
                slot_id: "", // vazio = manter dia actual
                tipo: tipoInicial,
                descricao: servico.descricao || "",
            });
        }
    }, [servico]);

    // Agrupa por dia (mesma lógica do AgendarDialog)
    const diasDisponiveis = React.useMemo(() => {
        const map = new Map();
        (slots || []).forEach(s => {
            const vagas = (s.capacidade || 0) - (s.reservados || 0);
            if (vagas <= 0) return;
            if (!map.has(s.data)) {
                map.set(s.data, { data: s.data, slot_id: s.id, vagas_total: vagas });
            } else {
                map.get(s.data).vagas_total += vagas;
            }
        });
        return Array.from(map.values()).sort((a, b) => (a.data > b.data ? 1 : -1));
    }, [slots]);

    async function save() {
        if (!form.moto_id) { toast.error("Escolha um veículo."); return; }
        if (form.tipo === "Outros" && !form.descricao.trim()) {
            toast.error("Descreva os serviços/sintomas quando escolhe 'Outros'.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                moto_id: form.moto_id,
                tipo: form.tipo,
                descricao: form.descricao,
            };
            if (form.slot_id) payload.slot_id = form.slot_id;
            await api.put(`/cliente/servicos/${servico.id}`, payload);
            toast.success("Marcação actualizada.");
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao reagendar");
        } finally { setSaving(false); }
    }

    if (!servico) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-sm" data-testid="reagendar-dialog">
                <DialogHeader><DialogTitle>Reagendar / editar marcação</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        Marcação actual: <span className="font-mono">{servico.data}</span>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Veículo</Label>
                        <Select value={form.moto_id} onValueChange={(v) => setForm({ ...form, moto_id: v })}>
                            <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="reagendar-moto">
                                <SelectValue placeholder="Selecionar" />
                            </SelectTrigger>
                            <SelectContent>
                                {motos.map(m => <SelectItem key={m.id} value={m.id}>{m.marca} {m.modelo}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Novo dia de entrega (opcional)</Label>
                        <Select value={form.slot_id} onValueChange={(v) => setForm({ ...form, slot_id: v })}>
                            <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="reagendar-slot">
                                <SelectValue placeholder="Manter dia actual" />
                            </SelectTrigger>
                            <SelectContent>
                                {diasDisponiveis.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-zinc-500">Sem dias disponíveis</div>
                                ) : diasDisponiveis.map(d => (
                                    <SelectItem key={d.slot_id} value={d.slot_id}>
                                        {d.data}  ({d.vagas_total} vaga{d.vagas_total !== 1 ? "s" : ""})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="mt-1 text-[10px] text-zinc-500">Deixe em branco para manter o dia actual.</p>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Tipo de serviço</Label>
                        <div className="mt-1.5 grid grid-cols-3 gap-2">
                            {CATEGORIAS_CLIENTE.map(cat => {
                                const active = form.tipo === cat;
                                return (
                                    <button
                                        key={cat}
                                        type="button"
                                        onClick={() => setForm({ ...form, tipo: cat })}
                                        data-testid={`reagendar-tipo-${cat.toLowerCase().replace(/\s/g, "-")}`}
                                        className={`h-10 border text-xs font-semibold transition-all ${
                                            active
                                                ? "border-[#FF3B30] bg-[#FF3B30] text-white"
                                                : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"
                                        }`}
                                    >
                                        {cat}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                            {form.tipo === "Outros" ? "Descreva os serviços e sintomas *" : "Notas adicionais (opcional)"}
                        </Label>
                        <Textarea rows={3} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} className="mt-1.5 rounded-sm" data-testid="reagendar-descricao" />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" className="rounded-sm" onClick={onClose}>Fechar</Button>
                    <Button onClick={save} disabled={saving} className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]" data-testid="reagendar-save">
                        {saving ? "A guardar..." : "Guardar alterações"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
